import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

import { db, auth } from "../firebaseConfig"; // ✅ asegúrate de exportar auth
import "./MisProductos.css";

const PAGE_SIZE = 150;

function formatCurrencyMXN(value) {
  const n = Number(value);
  if (!isFinite(n)) return "";
  return n.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}
function safeTwoLines(text) {
  if (!text) return "";
  return String(text);
}

export default function MisProductos() {
  const navigate = useNavigate();

  const [uid, setUid] = useState(undefined); // undefined = cargando sesión, null = no logueado

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);

  const [pageCursors, setPageCursors] = useState([null]);
  const [lastDocCurrentPage, setLastDocCurrentPage] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const [search, setSearch] = useState("");

  // ✅ Escuchar sesión real de Firebase
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUid(u?.uid || null);
    });
    return () => unsub();
  }, []);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;

    return items.filter((p) => {
      const titulo = (p.titulo || "").toLowerCase();
      const desc = (p.descripcion || "").toLowerCase();
      const cats = Array.isArray(p.categorias) ? p.categorias.join(",").toLowerCase() : "";
      return titulo.includes(s) || desc.includes(s) || cats.includes(s);
    });
  }, [items, search]);

  const noResultsMessage = useMemo(() => {
    if (!search.trim()) return "";
    if (filtered.length > 0) return "";
    return "No se encontró en esta página. Cambia de página para seguir buscando.";
  }, [search, filtered]);

  async function fetchPage(pageNumber, uidValue) {
    if (!uidValue) {
      setItems([]);
      setHasMore(false);
      setLastDocCurrentPage(null);
      return;
    }

    const isFirst = pageNumber === 1;
    const cursor = pageCursors[pageNumber - 1] ?? null;

    const baseRef = collection(db, "productos");

    let q = query(
      baseRef,
      where("vendedorId", "==", uidValue),
      orderBy("createdAt", "desc"),
      limit(PAGE_SIZE)
    );

    if (!isFirst && cursor) {
      q = query(
        baseRef,
        where("vendedorId", "==", uidValue),
        orderBy("createdAt", "desc"),
        startAfter(cursor),
        limit(PAGE_SIZE)
      );
    }

    const snap = await getDocs(q);
    const docs = snap.docs;

    setItems(docs.map((d) => ({ id: d.id, ...d.data() })));
    const last = docs.length > 0 ? docs[docs.length - 1] : null;
    setLastDocCurrentPage(last);
    setHasMore(docs.length === PAGE_SIZE);
  }

  // ✅ Cuando ya tenemos uid (real), cargamos página 1
  useEffect(() => {
    let alive = true;

    (async () => {
      if (uid === undefined) return; // aún cargando sesión

      try {
        setLoading(true);
        setPage(1);
        setPageCursors([null]);
        setSearch("");

        await fetchPage(1, uid);
      } catch (e) {
        console.error("MisProductos fetch error:", e);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  async function handleNext() {
    if (!uid) return;
    if (!hasMore || loadingMore) return;
    if (!lastDocCurrentPage) return;

    try {
      setLoadingMore(true);

      const nextPage = page + 1;

      setPageCursors((prev) => {
        const copy = [...prev];
        copy[nextPage - 1] = lastDocCurrentPage;
        return copy;
      });

      setPage(nextPage);
      setSearch("");

      const baseRef = collection(db, "productos");
      const q = query(
        baseRef,
        where("vendedorId", "==", uid),
        orderBy("createdAt", "desc"),
        startAfter(lastDocCurrentPage),
        limit(PAGE_SIZE)
      );

      const snap = await getDocs(q);
      const docs = snap.docs;
      setItems(docs.map((d) => ({ id: d.id, ...d.data() })));

      const last = docs.length > 0 ? docs[docs.length - 1] : null;
      setLastDocCurrentPage(last);
      setHasMore(docs.length === PAGE_SIZE);
    } catch (e) {
      console.error("Next page error:", e);
    } finally {
      setLoadingMore(false);
    }
  }

  async function handlePrev() {
    if (!uid) return;
    if (page <= 1 || loadingMore) return;

    try {
      setLoadingMore(true);

      const prevPage = page - 1;
      setPage(prevPage);
      setSearch("");

      setLoading(true);
      await fetchPage(prevPage, uid);
    } catch (e) {
      console.error("Prev page error:", e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  function goToDetails(productId) {
    navigate(`/admin/productos/editar/${productId}`);
  }

  // ✅ estados de sesión
  if (uid === undefined) {
    return (
      <div className="mpWrap">
        <div className="mpHeader">
          <h1 className="mpTitle">Mis productos</h1>
          <p className="mpSubtitle">Cargando sesión…</p>
        </div>
      </div>
    );
  }

  if (!uid) {
    return (
      <div className="mpWrap">
        <div className="mpHeader">
          <h1 className="mpTitle">Mis productos</h1>
          <p className="mpSubtitle">Inicia sesión para ver tus productos.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mpWrap">
      <div className="mpHeader">
        <div>
          <h1 className="mpTitle">Mis productos</h1>
          <p className="mpSubtitle">
            Mostrando {PAGE_SIZE} por página. Busca dentro de esta página.
          </p>
        </div>

        <div className="mpHeaderRight">
          <button
            type="button"
            className="mpCreateBtn"
            onClick={() => navigate("/admin/productos/nuevo")}
            disabled={loading || loadingMore}
            title="Crear producto (pendiente)"
          >
            + Crear producto
          </button>

          <div className="mpSearchBox">
            <input
              className="mpSearchInput"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por título, descripción o categoría…"
            />
            {search?.length > 0 && (
              <button className="mpClearBtn" onClick={() => setSearch("")}>
                Limpiar
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="mpPager">
        <button
          className="mpBtn"
          onClick={handlePrev}
          disabled={page <= 1 || loading || loadingMore}
        >
          ← Anterior
        </button>

        <div className="mpPageInfo">
          Página <b>{page}</b>
        </div>

        <button
          className="mpBtn mpBtnPrimary"
          onClick={handleNext}
          disabled={!hasMore || loading || loadingMore}
        >
          Siguiente →
        </button>
      </div>

      {loading ? (
        <div className="mpLoading">
          <div className="mpSpinner" />
          <div>Cargando productos…</div>
        </div>
      ) : (
        <>
          {noResultsMessage ? <div className="mpNotice">{noResultsMessage}</div> : null}

          {filtered.length === 0 ? (
            <div className="mpEmpty">
              <div className="mpEmptyCard">
                <h3>Sin productos aquí</h3>
                <p>
                  {search.trim()
                    ? "No hay coincidencias en esta página."
                    : "Aún no has creado productos, o no hay datos para mostrar."}
                </p>
              </div>
            </div>
          ) : (
            <div className="mpGrid">
              {filtered.map((p) => {
                const precioBase = Number(p.precio);
                const pv =
                  p.precioVendedor == null
                    ? (isFinite(precioBase) ? precioBase / 1.15 : null)
                    : Number(p.precioVendedor);

                const categorias = Array.isArray(p.categorias)
                  ? p.categorias.filter(Boolean).join(", ")
                  : "";

                return (
                  <button
                    key={p.id}
                    className="mpCard"
                    onClick={() => goToDetails(p.id)}
                    type="button"
                  >
                    <div className="mpImgWrap">
                      <img
                        className="mpImg"
                        src={p.imagenPrincipal || ""}
                        alt={p.titulo || "Producto"}
                        onError={(e) => {
                          e.currentTarget.src =
                            "https://via.placeholder.com/600x400?text=Sin+imagen";
                        }}
                      />
                    </div>

                    <div className="mpCardBody">
                      <div className="mpCardTop">
                        <div className="mpCardTitle" title={p.titulo || ""}>
                          {p.titulo || "Sin título"}
                        </div>
                        <div className="mpPrice">{pv == null ? "—" : formatCurrencyMXN(pv)}</div>
                      </div>

                      <div className="mpDesc">{safeTwoLines(p.descripcion || "Sin descripción")}</div>

                      {categorias ? (
                        <div className="mpCats" title={categorias}>
                          {categorias}
                        </div>
                      ) : (
                        <div className="mpCats mpCatsMuted">Sin categorías</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="mpFooterHint">
            Tip: la búsqueda es solo dentro de esta página. Si no aparece, cambia de página.
          </div>
        </>
      )}
    </div>
  );
}